import { useEffect, useState } from 'react';
import { SignupContractVersion } from '~/services/fetch-personalized-signup-contract-set-from-template';
import { trackError } from '~/utils/tracking';

interface UsePreSignedContractPdfResult {
  pdfUrl: string | null;
  isLoading: boolean;
  error: Error | null;
}

export const usePreSignedContractPdf = (
  contract: SignupContractVersion,
): UsePreSignedContractPdfResult => {
  const { base64PerchPersonalizedFile, presignedUrl, fileUrl } = contract;

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (base64PerchPersonalizedFile) {
      setPdfUrl(base64PerchPersonalizedFile);
      return;
    }

    if (presignedUrl) {
      setIsLoading(true);
      setError(null);

      fetch(presignedUrl)
        .then(res => {
          if (!res.ok) {
            throw new Error(`Failed to fetch contract from S3: ${res.status}`);
          }
          return res.arrayBuffer();
        })
        .then(buffer => {
          return new Promise<string>((resolve, reject) => {
            const blob = new Blob([buffer], { type: 'application/pdf' });
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(blob);
          });
        })
        .then(dataUrl => {
          setPdfUrl(dataUrl);
        })
        .catch(err => {
          trackError(err);
          setError(err instanceof Error ? err : new Error(String(err)));
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      trackError(new Error('No valid PDF source available for contract'));
      setError(new Error('No valid PDF source available for contract'));
    }
  }, [base64PerchPersonalizedFile, presignedUrl, fileUrl]);

  return { pdfUrl, isLoading, error };
};
